import { BrandingAssets } from './branding.types';

/**
 * Package/Plan Features
 */
export interface PackageFeatures {
  whitelabelEnabled: boolean;
  maxClients?: number;
  maxPosts?: number;
  maxTeamMembers?: number;
  aiTokens?: number;
  prioritySupport?: boolean;
}

/**
 * User Package/Plan
 */
export interface UserPackage {
  id: number;
  name: string;
  tier: 'free' | 'starter' | 'professional' | 'agency' | 'enterprise';
  features: PackageFeatures;
}

/**
 * User Team
 */
export interface UserTeam {
  id: number;
  name: string;
  logo: string | null;
  timezone: string;
  agencyId: number | null;
  packageId?: number | null;
  hasWhitelabelAddon?: boolean;
  variationsEnabled?: boolean;
  isAgencyTeam?: boolean;
  carteBlanche?: boolean;
  /** ISO time carte blanche was last switched off; content created before it and never sent for review needs no release */
  carteBlancheDisabledAt?: string | null;
}

/**
 * User Agency
 */
export interface UserAgency {
  id: number;
  name: string;
  logo: string | null;
  whitelabelEnabled?: boolean;
}

/**
 * God Mode Session
 * When an agency user enters a client team space
 */
export interface GodModeSession {
  sessionId: string;
  targetTeamId: number;
  targetTeamName: string;
  targetTeamIcon?: string | null;
  targetTeamColor?: string | null;
}

/**
 * Current Team (God Mode Context)
 */
export interface CurrentTeam {
  id: number;
  name: string;
  logo: string | null;
  variationsEnabled?: boolean;
  isAgencyTeam?: boolean;
  carteBlanche?: boolean;
  /** ISO time carte blanche was last switched off; content created before it and never sent for review needs no release */
  carteBlancheDisabledAt?: string | null;
}

/**
 * User Types
 */
export interface User {
  id: number;
  /** New accounts without an active Plan should start in the Shop. */
  needsPlan?: boolean;
  email: string;
  fullName: string;
  role: 'ADMIN' | 'USER';
  teamRole: 'OWNER' | 'ADMIN' | 'MEMBER' | 'REVIEWER' | null;
  isSuperAdmin: boolean;

  // Home team
  teamId: number | null;
  team: UserTeam | null;

  // God Mode context
  currentTeamId: number | null;
  currentTeam: CurrentTeam | null;

  // Agency staff info
  isAgencyStaff: boolean;
  agencyId: number | null;
  agencyRole: 'OWNER' | 'ADMIN' | 'MANAGER' | 'MEMBER' | null;
  /** Effective brand scope in the acting agency (OWNER/ADMIN true; limited manager / member false). */
  agencySeesAllBrands?: boolean;
  agency: UserAgency | null;
  /** True only when the user is agency staff AND has the agency-wide reviewer flag. */
  agencyIsReviewer: boolean;
  /** @deprecated Old name of `agencyIsReviewer` (kept one release by the API). */
  agencyCanApprove?: boolean;

  // God Mode session
  godModeSession: GodModeSession | null;

  // Impersonation session (from JWT)
  impersonationSession?: {
    id: string;
    adminUserId: number;
    adminEmail: string;
    adminName: string | null;
    targetUserId: number;
    targetEmail: string;
    targetName: string | null;
    reason: string | null;
    isReadOnly: boolean;
    enteredAt: string;
    expiresAt: string;
  } | null;

  emailVerified: boolean;

  // True when the active brand space's portfolio package allows more than 1 brand
  // (i.e. upgrades/tokens are managed collectively at the portfolio level).
  portfolioIsMultiBrand?: boolean;

  timezone?: string | null;
  preferences?: Record<string, unknown> | null;
  preferredLanguage?: string;
  createdAt?: string;
  package?: UserPackage;
}

/**
 * Authentication Response Types
 */
export interface AuthResponse {
  accessToken: string;
  user: User;
}

/**
 * Authentication Request Types
 */
export interface RegisterRequest {
  email: string;
  password: string;
  fullName: string;
  teamName: string;
  /** Honeypot field — always empty for real users; bots fill it */
  website?: string;
  /** UTM source captured from ?utm_source= at page load (invisible plumbing) */
  utmSource?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

/**
 * API Error Response
 */
export interface ApiError {
  message: string;
  statusCode: number;
  code?: string;
  errors?: Record<string, string[]>;
}

/**
 * Auth Error Codes
 */
export const AUTH_ERROR_CODES = {
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  ACCOUNT_INACTIVE: 'ACCOUNT_INACTIVE',
  ACCOUNT_ACCESS_REVOKED: 'ACCOUNT_ACCESS_REVOKED',
  EMAIL_EXISTS: 'EMAIL_EXISTS',
  WEAK_PASSWORD: 'WEAK_PASSWORD',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  UNAUTHORIZED: 'UNAUTHORIZED',
} as const;

/**
 * User-friendly error messages for auth error codes
 */
export const AUTH_ERROR_MESSAGES: Record<string, string> = {
  [AUTH_ERROR_CODES.INVALID_CREDENTIALS]: 'The email or password you entered is incorrect. Please try again.',
  [AUTH_ERROR_CODES.ACCOUNT_INACTIVE]: 'Your account has been deactivated. Please contact support for assistance.',
  [AUTH_ERROR_CODES.ACCOUNT_ACCESS_REVOKED]: 'Your account access has been revoked. Please contact your team administrator.',
  [AUTH_ERROR_CODES.EMAIL_EXISTS]: 'An account with this email already exists. Try logging in instead.',
  [AUTH_ERROR_CODES.WEAK_PASSWORD]: 'Password is too weak. Please use at least 8 characters with a mix of letters, numbers, and symbols.',
  [AUTH_ERROR_CODES.TOKEN_EXPIRED]: 'Your session has expired. Please log in again.',
  [AUTH_ERROR_CODES.TOKEN_INVALID]: 'Invalid session. Please log in again.',
  [AUTH_ERROR_CODES.UNAUTHORIZED]: 'You are not authorized to perform this action.',
};

/**
 * Auth Context Types
 */
export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * True when the boot-time auth check (token refresh / `/me`) failed due to
   * a transient network error or timeout — NOT a genuine 401/403 session
   * rejection. While true, any existing session must be treated as still
   * potentially valid (do not redirect to /login); show a retry affordance
   * and call `retryAuthInit()` instead.
   */
  authConnectionError: boolean;
  /**
   * True while the token-refresh circuit breaker (api.ts) is open due to
   * repeated non-auth refresh failures happening MID-SESSION (app already
   * mounted, e.g. a composer draft open). Unlike `authConnectionError`, this
   * must NEVER drive a route guard / full-screen takeover — it only backs a
   * non-destructive banner (see `ReconnectBanner`). Use `retryConnection()`
   * to manually retry.
   */
  connectionDegraded: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<User>;
  loginWithResponse: (accessToken: string, user: User) => void;
  onTeamContextChange: (previousTeamId: number | null) => Promise<void>;
  /** Manually retry the boot-time auth check after `authConnectionError`. */
  retryAuthInit: () => Promise<void>;
  /**
   * Manually retry the connection after `connectionDegraded` — a one-shot,
   * non-destructive refresh attempt. Does NOT touch `isLoading` and does not
   * unmount anything (safe to call with a composer modal open).
   */
  retryConnection: () => Promise<void>;
}

/**
 * Theme & Branding Types
 *
 * BrandingConfig matches the backend API response structure
 * Backend sends camelCase fields (primary, secondary, etc.)
 */
export interface BrandingConfig {
  logo?: string; // Computed from assets.logoBig or assets.logoSmall
  logoSmall?: string; // For nav/small contexts
  colors: {
    primary: string;
    secondary: string;
    secondaryRgba?: string;
    gray?: string;
    gray2?: string;
    gray400?: string;
    gray600?: string;
    grayStone?: string;
    light?: string;
    calendarHighlight?: string;
    blackButton?: string;
    coral?: string;
    softCoral?: string;
    softMint?: string;
    peach?: string;
    lightTealGray?: string;
    sageGray?: string;
    warmGray?: string;
    offWhite?: string;
  };
  fontFamily?: string;
  fontUrl?: string; // Google Fonts URL
  headlineFontFamily?: string;
  headlineFontUrl?: string;
  assets?: BrandingAssets; // Full assets object from backend
  isWhitelabelEnabled?: boolean;
  workspaceName?: string; // Deprecated - keep for backward compatibility
  organizationName?: string; // Deprecated - keep for backward compatibility
}

export interface ThemeContextType {
  branding: BrandingConfig;
  isLoading: boolean;
  refreshBranding: () => Promise<void>;
}

/**
 * Re-export types from other type files
 */
export * from './calendar';
export * from './calendar-mappings';
export * from './posts';
export * from './approval';
export * from './notification';
export * from './agency';
export * from './branding.types';
export * from './labels';
export * from './mapping-rules';
export * from './scheduled-instances';
export * from './campaign-instances';
export * from './invitations';
export * from './campaign-templates';
export * from './evergreen';
export * from './package';
export * from './purchase';
export * from './status';
